/**
 * calendarApi.ts — shapes of the content calendar endpoints (GET /calendar,
 * POST /calendar/:id/reschedule), shared by the Worker and the admin UI.
 */
import { z } from "zod";
import { contentStatusSchema, contentTypeSchema, publishTargetSchema } from "./enums.ts";
import { calendarEventKindSchema } from "./calendarEvents.ts";

/** A holiday/event/note on the calendar for one year (see calendarEvents.ts). */
export const eventOccurrenceSchema = z.object({
  id: z.string(),
  title: z.string(),
  kind: calendarEventKindSchema,
  startDate: z.string(),
  endDate: z.string(),
  repeatsYearly: z.boolean(),
});

export const approvalStateSchema = z.enum(["valid", "missing", "stale"]);

/** One item on the calendar. Deliberately light: no body text. */
export const calendarEntrySchema = z.object({
  id: z.string(),
  type: contentTypeSchema,
  status: contentStatusSchema,
  title: z.string(),
  slug: z.string(),
  campaign: z.string().nullable(),
  publishTarget: publishTargetSchema.nullable(),
  /** When it is due (scheduled/failed) — ISO UTC. */
  scheduledFor: z.string().nullable(),
  /** Planning date for drafts / in-review items (not a schedule) — ISO UTC. */
  plannedFor: z.string().nullable(),
  /** When it actually went live (published) — ISO UTC. */
  publishedAt: z.string().nullable(),
  publishAttempts: z.number().int(),
  publishNextAttemptAt: z.string().nullable(),
  publishLastError: z.string().nullable(),
  /** Plain-sentence reason the cron is holding this item, if it is. */
  publishBlockedReason: z.string().nullable(),
  /** Whether the manager's approval still covers the current text (scheduled items only). */
  approval: approvalStateSchema.nullable(),
  authorName: z.string().nullable(),
  /** AI-originated drafts are marked so reviewers know. */
  aiOrigin: z.boolean(),
});
export type CalendarEntry = z.infer<typeof calendarEntrySchema>;

export const calendarQuerySchema = z.object({
  siteId: z.string().min(1),
  /** Inclusive start / exclusive end of the visible window, ISO UTC. */
  from: z.string().datetime(),
  to: z.string().datetime(),
});
export type CalendarQuery = z.infer<typeof calendarQuerySchema>;

export const calendarResponseSchema = z.object({
  /** Scheduled, failed and published items relevant to the window. */
  items: z.array(calendarEntrySchema),
  /** Drafts / in-review items that have no date yet, to drag onto the calendar. */
  unscheduled: z.array(calendarEntrySchema),
  settings: z.object({
    autoPublishEnabled: z.boolean(),
    dailyCap: z.number().int(),
    weeklyCap: z.number().int(),
    timezone: z.string().default("UTC"),
    weekStartsOn: z.union([z.literal(0), z.literal(1)]).default(1),
  }),
  usage: z.object({ publishedToday: z.number().int(), publishedThisWeek: z.number().int() }),
  /** Holidays, events and notes touching the window, as dates in the site's calendar. */
  events: z.array(eventOccurrenceSchema).default([]),
  campaigns: z.array(z.string()),
  serverTime: z.string(),
});
export type CalendarResponse = z.infer<typeof calendarResponseSchema>;

export const rescheduleBodySchema = z.object({
  scheduledFor: z.string().datetime({ message: "scheduledFor must be a valid ISO 8601 UTC timestamp." }),
  /** Optionally move the item to the other environment at the same time. */
  target: publishTargetSchema.optional(),
});
export type RescheduleBody = z.infer<typeof rescheduleBodySchema>;

/** Pin a draft / in-review item to a day (or clear the pin with null). */
export const planBodySchema = z.object({
  plannedFor: z.string().datetime({ message: "plannedFor must be a valid ISO 8601 UTC timestamp." }).nullable(),
});

/** Create a draft straight from the calendar. Only a title is required. */
export const quickCreateBodySchema = z.object({
  siteId: z.string().min(1),
  type: contentTypeSchema.default("article"),
  title: z.string().trim().min(3, "Give it a title of at least 3 characters").max(200),
  campaign: z.string().trim().max(80).nullable().optional(),
  plannedFor: z.string().datetime().nullable().optional(),
});
export type QuickCreateBody = z.infer<typeof quickCreateBodySchema>;

export const duplicateBodySchema = z.object({
  plannedFor: z.string().datetime().nullable().optional(),
});

export const BULK_ACTIONS = ["shift", "unschedule", "delete"] as const;
export const bulkBodySchema = z
  .object({
    ids: z.array(z.string().min(1)).min(1).max(100),
    action: z.enum(BULK_ACTIONS),
    /** For "shift": move by this many days (negative = earlier). Planned and scheduled items only. */
    days: z.number().int().min(-365).max(365).optional(),
  })
  .refine((b) => b.action !== "shift" || (b.days !== undefined && b.days !== 0), { message: "Say how many days to move the items", path: ["days"] });
export type BulkBody = z.infer<typeof bulkBodySchema>;

export const bulkResultSchema = z.object({
  done: z.array(z.string()),
  failed: z.array(z.object({ id: z.string(), title: z.string(), reason: z.string() })),
});
export type BulkResult = z.infer<typeof bulkResultSchema>;

export const idResponseSchema = z.object({ id: z.string() });
